import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BeatLoader } from "react-spinners";
import tailwindClassnames from "../common/TailwindClassnames";
import { isTopLayerOpen } from "../common/topLayer";
import SpeciesPicker from "./SpeciesPicker";
import { EditFields, SpeciesOption } from "./reviewActions";

export type ObservationEditorInitial = {
  taxonId: number | null;
  label: string | null;
  individualCount: number | null;
  comments: string | null;
};

type ObservationEditorProps = {
  /** Modal title (context-dependent, supplied by the surface). */
  title: string;
  /** Project scope for the species picker. */
  projectId: number;
  /** Values the editor opens with. */
  initial: ObservationEditorInitial;
  /**
   * Called with the confirmed fields (taxon guaranteed non-null), plus the
   * display label of the chosen species.
   *
   * The label is a second argument rather than part of `EditFields` because no
   * commit endpoint wants it — only the feed's bulk add-observation confirmation
   * does, which has to name the species back to the reviewer and has no taxonomy
   * lookup of its own (AKL-753). Callers that don't need it simply take one
   * parameter.
   */
  onConfirm: (fields: EditFields, label: string | null) => void;
  /** Called when the editor is dismissed without confirming. */
  onCancel: () => void;
  /** True while the triggering action is committing (disables confirm). */
  busy?: boolean;
  /**
   * Optional note rendered under the count field and wired to it with
   * `aria-describedby`.
   *
   * Added for AKL-774, where the count opens prefilled from the number of
   * bounding boxes the reviewer ticked - a number they may well want to change,
   * and one that needs explaining because it is not the number of cards shown
   * in the title. Omitted by the four original contexts, which render nothing
   * extra.
   */
  countHelp?: string;
  /**
   * Options the species picker offers before the reviewer types anything.
   *
   * Added for AKL-781, and — like `countHelp` — passed only by the
   * assign-species context, which seeds the menu with the distinct taxa
   * already sitting on the ticked suggestion cards. The other four contexts
   * omit it and open with an empty menu exactly as before.
   *
   * An offer, not a preselection: `initial.taxonId` is still what decides
   * whether anything is selected, and the assign-species context deliberately
   * leaves it null even when this list has one entry.
   */
  seededSpeciesOptions?: SpeciesOption[];
};

/**
 * The observation editor (§6.3), used in five contexts: edit-suggestion-card
 * (aggregate), add-manual-observation, edit-existing-observation, per-object
 * edit, and assign-species (AKL-774) — the last of which is the only caller that
 * passes `countHelp` or `seededSpeciesOptions`.
 * Fields: species picker (effective list + WI fallback), integer count,
 * and optional comments. `observation_type` is derived server-side and is not an
 * editor field. Rendered as a full-screen modal over the theater; because it
 * covers the surface's controls and the surface's key handler bails while it is
 * open, the reviewer must resolve (save/cancel) it before navigating away — so
 * there is no separate unsaved-changes prompt (§5.5).
 */
const ObservationEditor: React.FC<ObservationEditorProps> = ({
  title,
  projectId,
  initial,
  onConfirm,
  onCancel,
  busy,
  countHelp,
  seededSpeciesOptions,
}) => {
  const { t } = useTranslation();
  const [taxonId, setTaxonId] = useState<number | null>(initial.taxonId);
  const [taxonLabel, setTaxonLabel] = useState<string | null>(initial.label);
  // Preserve the initial count exactly (which may be null for a blank/unknown
  // observation) — the surface already supplies 1 as the Add-observation
  // default, so coercing null→1 here would silently overwrite an existing
  // null count on save.
  const [count, setCount] = useState<number | null>(initial.individualCount);
  const [comments, setComments] = useState<string>(initial.comments ?? "");

  // Whether any field changed from the values the editor opened with — gates
  // the confirm button so an unchanged editor can't post a no-op edit.
  const dirty =
    taxonId !== initial.taxonId ||
    count !== initial.individualCount ||
    comments !== (initial.comments ?? "");

  // Dismiss the editor unless an action is committing — while busy the Cancel
  // button is disabled, so Escape / backdrop / × must be inert too, otherwise
  // the editor could close mid-commit and read as a cancelled action.
  const dismiss = () => {
    if (busy) return;
    onCancel();
  };

  // Escape cancels the editor (handled here, not via the surface's global
  // shortcut listener, so it works while a field is focused).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // A dialog layered above the editor owns Escape while it is open (AKL-798). Without
      // this guard, this capture-phase `window` listener runs before React's delegated
      // handler on that dialog's panel and cancels THIS editor — losing the species, count
      // and comments staged in it — so one Escape closed the dialog above and this with it.
      if (isTopLayerOpen()) return;
      if (e.key === "Escape") {
        e.stopPropagation();
        if (busy) return;
        onCancel();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onCancel, busy]);

  // Require a taxon, not-busy, AND an actual change: submitting an unchanged
  // editor would post a no-op edit that appends meaningless "edited" review rows
  // (the API can't reject it because the editor always sends `comments`, so its
  // no-op guard never sees a "nothing provided" request). For Add, selecting a
  // taxon from the empty initial state already makes the editor dirty.
  const canConfirm = taxonId != null && !busy && dirty;

  const handleConfirm = () => {
    if (taxonId == null) return;
    onConfirm(
      {
        taxonId,
        individualCount: count,
        comments: comments.trim() === "" ? null : comments.trim(),
      },
      taxonLabel,
    );
  };

  return (
    <div
      className={`${tailwindClassnames.modalOverlay} z-[1200]`}
      onClick={dismiss}
      data-testid="observation-editor"
    >
      <div
        className={`${tailwindClassnames.modalPanel} max-w-md`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={tailwindClassnames.modalHeaderRow}>
          <h2 className={tailwindClassnames.modalTitle}>{title}</h2>
          <button
            type="button"
            className={tailwindClassnames.modalCloseButton}
            onClick={dismiss}
            disabled={busy}
            aria-label={t("Close")}
            data-testid="observation-editor-close"
          >
            &times;
          </button>
        </div>

        <div className={`${tailwindClassnames.modalBody} flex flex-col gap-4`}>
          <div>
            <SpeciesPicker
              projectId={projectId}
              value={taxonId}
              valueLabel={taxonLabel}
              onChange={(id, label) => {
                setTaxonId(id);
                setTaxonLabel(label);
              }}
              disabled={busy}
              seededOptions={seededSpeciesOptions}
            />
          </div>

          <div className="flex flex-col">
            <label
              className={tailwindClassnames.dropdownLabel}
              htmlFor="observation-count"
            >
              {t("Count")}
            </label>
            <input
              id="observation-count"
              type="number"
              min={1}
              className={tailwindClassnames.detailsPageTextInput}
              value={count ?? ""}
              onChange={(e) => {
                // Keep count a positive integer: empty clears it (null, valid
                // for blank/unknown); otherwise floor to an int and clamp to
                // >= 1; unparseable input clears it — so NaN/decimals/<1 are
                // never sent to the API.
                const raw = e.target.value;
                if (raw === "") {
                  setCount(null);
                  return;
                }
                const n = Math.floor(Number(raw));
                setCount(Number.isFinite(n) ? Math.max(1, n) : null);
              }}
              disabled={busy}
              aria-describedby={
                countHelp ? "observation-count-help" : undefined
              }
              data-testid="observation-count"
            />
            {countHelp && (
              <p
                id="observation-count-help"
                className="mt-1 text-xs text-off-black-light/70 dark:text-primary-off-white/50"
                data-testid="observation-count-help"
              >
                {countHelp}
              </p>
            )}
          </div>

          <div className="flex flex-col">
            <label
              className={tailwindClassnames.dropdownLabel}
              htmlFor="observation-comments"
            >
              {t("Comments")}
            </label>
            <textarea
              id="observation-comments"
              className={tailwindClassnames.detailsPageTextArea}
              rows={3}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              disabled={busy}
              data-testid="observation-comments"
            />
          </div>
        </div>

        <div className={tailwindClassnames.modalFooter}>
          <button
            type="button"
            className={tailwindClassnames.btnSecondary}
            onClick={onCancel}
            disabled={busy}
            data-testid="observation-editor-cancel"
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            className={`relative ${tailwindClassnames.btnPrimary}`}
            onClick={handleConfirm}
            disabled={!canConfirm}
            data-testid="observation-editor-confirm"
          >
            {/* Keep the label in the layout (just hidden) while busy so the
                button — and the surrounding button row — don't resize when the
                spinner replaces the text; the spinner is overlaid centered on
                top and uses currentColor so it reads on the primary fill in
                both light and dark mode. */}
            <span className={busy ? "invisible" : undefined}>
              {t("Save Observation")}
            </span>
            {busy && (
              <span className="absolute inset-0 flex items-center justify-center">
                <BeatLoader
                  color="currentColor"
                  loading={true}
                  size={10}
                  margin={5}
                />
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ObservationEditor;
